import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { db } from '../firebase/firebase';
import { collection, addDoc, serverTimestamp, getDocs } from 'firebase/firestore';
import { 
  ArrowLeft, Calendar, Clock, MapPin, Check, ChevronDown, ChevronUp,
  Tag, Star, User, Phone, Mail, Shield, Gift, Navigation
} from 'lucide-react';

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
const fmt = (n) => Math.round(n).toLocaleString('en-IN');

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
export default function ContactDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { bookingData, setBookingData } = useBooking();
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* ── merged data from vehicle page ── */
  const data = { ...bookingData, ...(location.state || {}) };
  const handleBack = () => navigate('/vehicle-selection', { state: data, replace: true });
  const vehicle = data.selectedVehicle || {};
  const priceBreakdown = data.priceBreakdown || {};
  const isRoundTrip = data.tripType === 'roundtrip';

  const baseTripFare = priceBreakdown.tripFare || 0;
  const driverAllowance = priceBreakdown.driverAllowance || 0;
  const extraCharges = priceBreakdown.extraCharges || 0;
  const estimatedTax = priceBreakdown.estimatedTax || 0;
  const totalFare = priceBreakdown.totalFare || 0;

  /* ── form state ── */
  const [form, setForm] = useState({
    fullName: '', mobile: '', countryCode: '+91',
    email: '', altEmail: '', gstin: '',
    pickupInstructions: ''
  });
  const [errors, setErrors] = useState({});
  const [showAlt, setShowAlt] = useState(false);
  const [showGST, setShowGST] = useState(false);
  const [addons, setAddons] = useState([]);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [bookingId, setBookingId] = useState('');
  const [fareOpen, setFareOpen] = useState(true);

  const addonList = [
    { id: 'expressway', label: `${data.from || 'Bangalore'} – ${data.to || 'Mysore'} Expressway`, price: 415, badge: 'Most Popular' },
    { id: 'luggage', label: 'Cab with Luggage Carrier', price: 149 },
  ];

  const addonSum = addons.reduce((s, id) => s + (addonList.find(a => a.id === id)?.price || 0), 0);
  const grandTotal = totalFare + addonSum;

  const toggleAddon = (id) => setAddons(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = 'Name required';
    if (!/^\d{10}$/.test(form.mobile)) e.mobile = 'Valid 10-digit number required';
    if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleProceed = async () => {
    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const generateBookingId = async () => {
        try {
          const bookingsRef = collection(db, 'bookings');
          const querySnapshot = await getDocs(bookingsRef);
          const totalBookings = querySnapshot.size;
          const nextNumber = totalBookings + 1;
          const paddedNumber = String(nextNumber).padStart(4, '0');
          return `BOOKININDIA-${paddedNumber}`;
        } catch (error) {
          const timestamp = Date.now();
          return `BOOKININDIA-${timestamp}`;
        }
      };

      const newBookingId = await generateBookingId();

      const bookingDetails = {
        bookingId: newBookingId,
        status: 'confirmed',
        createdAt: serverTimestamp(),

        customer: {
          fullName: form.fullName,
          mobile: `${form.countryCode}${form.mobile}`,
          email: form.email,
          altEmail: form.altEmail || null,
          gstin: form.gstin || null,
          pickupInstructions: form.pickupInstructions || null
        },

        trip: {
          from: data.from || 'Bangalore',
          to: data.to || 'Mysore',
          tripType: data.tripType || 'oneway',
          pickupDate: data.pickupDate,
          pickupTime: data.pickupTime,
          returnDate: data.returnDate || null,
          returnTime: data.returnTime || null,
          distance: data.distance || null,
          isInterState: data.isInterState || false
        },

        vehicle: {
          id: vehicle.id,
          name: vehicle.name,
          type: vehicle.type,
          seaters: vehicle.seaters,
          image: data.vehicleDetails?.image || null,
          rating: data.vehicleDetails?.rating || null
        },

        fare: {
          baseTripFare: baseTripFare,
          driverAllowance: driverAllowance,
          extraCharges: extraCharges,
          estimatedTax: estimatedTax,
          addons: addons.map(id => ({
            id: id,
            name: addonList.find(a => a.id === id)?.label,
            price: addonList.find(a => a.id === id)?.price
          })),
          addonTotal: addonSum,
          totalFare: grandTotal
        },

        payment: {
          method: 'offline',
          status: 'pending',
          amountToCollect: grandTotal,
          paymentInstructions: 'Payment to be made to the driver'
        }
      };

      const docRef = await addDoc(collection(db, 'bookings'), bookingDetails);

      const updated = {
        ...data,
        contactDetails: form,
        selectedAddons: addons,
        addonTotal: addonSum,
        grandTotal,
        bookingId: newBookingId,
        firestoreId: docRef.id
      };

      setBookingData(updated);
      setBookingId(newBookingId);
      setBookingConfirmed(true);

      setTimeout(() => {
        navigate('/booking-success', {
          state: {
            bookingId: newBookingId,
            customer: form,
            trip: updated,
            total: grandTotal,
            bookingDetails: bookingDetails
          }
        });
      }, 1500);

    } catch (error) {
      console.error('Error saving booking:', error);
      alert('There was an error confirming your booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (bookingConfirmed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-white">
        <div className="text-center p-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
            <Check size={40} className="text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Booking Confirmed!</h2>
          <p className="text-gray-600 mb-4">Your booking ID: <strong>{bookingId}</strong></p>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Syne:wght@500;600;700;800&display=swap');
        
        * { font-family: 'DM Sans', sans-serif; }
        html, body { overflow-x: hidden; }
        
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        
        input:focus, textarea:focus, select:focus {
          outline: none;
        }
        
        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 4px;
        }
        
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: #f97316;
          border-radius: 10px;
        }
      `}</style>

      {/* ========== DESKTOP VIEW - ORIGINAL UI ========== */}
      <div className="hidden md:block">
        <div className="cd-root">
          {/* Desktop Header */}
          <div className="cd-header">
            <div className="cd-header-inner">
              <button className="cd-header-back" onClick={handleBack}>
                <ArrowLeft size={14} /> Back
              </button>
              <span className="cd-header-title">Review Your Booking</span>
              <div className="cd-header-pill" style={{ marginLeft: 'auto' }}>
                <Shield size={12} /> Offline Payment
              </div>
            </div>
          </div>

          {/* Free cancel banner */}
          <div className="cd-free-cancel">
            <span style={{ fontSize: 16 }}>⏱️</span>
            <strong>Free cancellation till 1 hr of departure</strong>
          </div>

          {/* Body */}
          <div className="cd-body">
            {/* LEFT COLUMN */}
            <div>
              {/* Booking Summary Strip */}
              <div className="cd-summary-strip">
                <div className="route">
                  <span>{data.from || 'Bangalore (Bengaluru)'}</span>
                  <span className="arrow">→</span>
                  <span>{data.to || 'Mysore'}</span>
                  <span style={{ fontSize: 13, fontWeight: 400, color: '#94a3b8', marginLeft: 4 }}>
                    ({isRoundTrip ? 'Roundtrip' : 'Oneway'})
                  </span>
                </div>
                <div className="sub">
                  Car Type: <strong style={{ color: '#e2e8f0' }}>{vehicle.name || 'Sedan'}</strong> &nbsp;·&nbsp;
                  Pickup: <strong style={{ color: '#e2e8f0' }}>{data.pickupDate || '—'} {data.pickupTime || ''}</strong>
                  {data.distance && <> &nbsp;·&nbsp; <strong style={{ color: '#e2e8f0' }}>{data.distance} kms included</strong></>}
                </div>
                <div className="cd-chips">
                  {data.pickupDate && <span className="cd-chip"><Calendar size={11} />{data.pickupDate}</span>}
                  {data.pickupTime && <span className="cd-chip"><Clock size={11} />{data.pickupTime}</span>}
                  {data.distance && <span className="cd-chip"><MapPin size={11} />{data.distance} km</span>}
                  {isRoundTrip && data.returnDate && <span className="cd-chip"><Calendar size={11} />Return: {data.returnDate}</span>}
                  {data.isInterState && <span className="cd-chip" style={{ color: '#c4b5fd' }}>Inter-State</span>}
                </div>
              </div>

              {/* Contact Details Card */}
              <div className="cd-card">
                <div className="cd-card-head">
                  <div className="cd-card-head-icon" style={{ background: '#fff3e6' }}>
                    <User size={16} style={{ color: '#f97316' }} />
                  </div>
                  <h2>Contact Details</h2>
                </div>
                <div className="cd-card-body">
                  <div className="cd-form-grid">
                    <div className="cd-field full">
                      <label>Full Name *</label>
                      <input className={`cd-input${errors.fullName ? ' err' : ''}`}
                        placeholder="Enter your full name"
                        value={form.fullName}
                        onChange={e => setForm(p => ({ ...p, fullName: e.target.value }))} />
                      {errors.fullName && <div className="cd-err-msg">{errors.fullName}</div>}
                    </div>
                    
                    <div className="cd-field">
                      <label>Mobile Number *</label>
                      <div className="cd-phone-row">
                        <select value={form.countryCode} onChange={e => setForm(p => ({ ...p, countryCode: e.target.value }))}>
                          <option>+91</option><option>+1</option><option>+44</option><option>+61</option>
                        </select>
                        <input className={`cd-input${errors.mobile ? ' err' : ''}`}
                          placeholder="10-digit number"
                          value={form.mobile} maxLength={10}
                          onChange={e => setForm(p => ({ ...p, mobile: e.target.value.replace(/\D/, '') }))} />
                      </div>
                      {errors.mobile && <div className="cd-err-msg">{errors.mobile}</div>}
                    </div>
                    
                    <div className="cd-field full">
                      <label>Email ID *</label>
                      <input className={`cd-input${errors.email ? ' err' : ''}`}
                        type="email" placeholder="your@email.com"
                        value={form.email}
                        onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
                      {errors.email && <div className="cd-err-msg">{errors.email}</div>}
                      <div className="cd-links-row">
                        <button className="cd-link-btn" onClick={() => setShowAlt(p => !p)}>+ Alternate email</button>
                        <button className="cd-link-btn" onClick={() => setShowGST(p => !p)}>+ Add GST</button>
                      </div>
                    </div>
                    
                    {showAlt && (
                      <div className="cd-field full">
                        <label>Alternate Email</label>
                        <input className="cd-input" type="email" placeholder="alternate@email.com"
                          value={form.altEmail}
                          onChange={e => setForm(p => ({ ...p, altEmail: e.target.value }))} />
                      </div>
                    )}
                    
                    {showGST && (
                      <div className="cd-field full">
                        <label>GSTIN</label>
                        <input className="cd-input" placeholder="Enter GSTIN number"
                          value={form.gstin}
                          onChange={e => setForm(p => ({ ...p, gstin: e.target.value }))} />
                      </div>
                    )}
                    
                    <div className="cd-field full">
                      <label>Pickup Instructions (Optional)</label>
                      <textarea className="cd-input" rows="2"
                        placeholder="Any specific instructions for pickup location, landmarks, etc."
                        value={form.pickupInstructions}
                        onChange={e => setForm(p => ({ ...p, pickupInstructions: e.target.value }))} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Add-ons Card */}
              <div className="cd-card">
                <div className="cd-card-head">
                  <div className="cd-card-head-icon" style={{ background: '#f0fdf4' }}>
                    <Gift size={16} style={{ color: '#16a34a' }} />
                  </div>
                  <h2>Personalize Your Journey</h2>
                  <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 4 }}>Enhance your travel experience</span>
                </div>
                <div className="cd-card-body">
                  {addonList.map(a => (
                    <div key={a.id} className={`addon-item${addons.includes(a.id) ? ' active' : ''}`}
                      onClick={() => toggleAddon(a.id)}>
                      <div className="addon-check">
                        {addons.includes(a.id) && <Check size={12} style={{ color: '#fff' }} />}
                      </div>
                      <span className="addon-label">
                        {a.label}
                        {a.badge && <span className="addon-badge">{a.badge}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="cd-right">
              <div className="cd-pay-card">
                <div className="cd-pay-head">Booking Summary</div>
                <div className="cd-pay-body">
                  {/* Vehicle mini */}
                  <div className="cd-vehicle-mini">
                    {data.vehicleDetails?.image
                      ? <img src={data.vehicleDetails.image} alt={vehicle.name} />
                      : <span style={{ fontSize: 28 }}>🚗</span>}
                    <div>
                      <div className="vname">{vehicle.name || 'Selected Vehicle'}</div>
                      <div className="vtype">{vehicle.type || vehicle.seaters + ' Seater'}</div>
                    </div>
                    {data.vehicleDetails?.rating && (
                      <div className="vrating">
                        <Star size={10} /> {data.vehicleDetails.rating}
                      </div>
                    )}
                  </div>

                  {/* Payment Info */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ background: '#f0fdf4', padding: '12px', borderRadius: '12px', marginBottom: 16 }}>
                      <div style={{ fontSize: 13, color: '#166534', fontWeight: 600, marginBottom: 6 }}>
                        💰 Payment to be made to driver
                      </div>
                      <div style={{ fontSize: 12, color: '#15803d' }}>
                        No online payment required. Please pay the total amount directly to the driver at the time of pickup.
                      </div>
                    </div>
                  </div>

                  <button 
                    className="cd-proceed-btn" 
                    onClick={handleProceed}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Confirming Booking...' : 'CONFIRM BOOKING'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== MOBILE UI - APP STYLE ========== */}
      <div className="block md:hidden">
        <div className="min-h-screen bg-gray-100 overflow-x-hidden -mt-16">
          {/* Status Bar Spacer */}
          {/* <div className="h-12 bg-[#e71204]"></div> */}

          {/* Header with Back Button */}
          <div className="bg-[#e71204] px-4 pt-2 pb-6">
            <div className="flex items-center justify-between">
              <button
                onClick={handleBack}
                className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center active:scale-95 transition"
              >
                <ArrowLeft size={20} className="text-white" />
              </button>
              <h1 className="text-white font-bold text-lg font-syne">Confirm Booking</h1>
              <div className="w-10"></div>
            </div>

            {/* Trip Summary Card */}
            <div className="mt-4 bg-white/10 backdrop-blur-lg rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-white/70 text-xs">FROM</p>
                  <p className="text-white font-semibold text-sm truncate">{data.from || 'Bangalore'}</p>
                </div>
                <div className="px-2">
                  <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center">
                    <Navigation size={14} className="text-white" />
                  </div>
                </div>
                <div className="flex-1 text-right">
                  <p className="text-white/70 text-xs">TO</p>
                  <p className="text-white font-semibold text-sm truncate">{data.to || 'Mysore'}</p>
                </div>
              </div>
              <div className="flex justify-between mt-3 pt-2 border-t border-white/20">
                <div className="flex items-center gap-1">
                  <Calendar size={12} className="text-white/80" />
                  <span className="text-white text-xs">{data.pickupDate || '—'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock size={12} className="text-white/80" />
                  <span className="text-white text-xs">{data.pickupTime || '—'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <MapPin size={12} className="text-white/80" />
                  <span className="text-white text-xs">{data.distance ? `${data.distance} km` : '—'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content - Scrollable */}
          <div className="px-4 pb-28 -mt-4">
            {/* Vehicle Card */}
            <div className="bg-white rounded-2xl shadow-lg p-4 mb-4 animate-slideUp">
              <div className="flex items-center gap-3">
                <div className="w-16 h-18 rounded-xl overflow-hidden shadow-md border">
                  <img 
                    src={vehicle.image} 
                    alt="vehicle"
                    className="w-auto h-6 object-cover hover:scale-105 transition"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-800">{vehicle.name || 'Selected Vehicle'}</h3>
                  <p className="text-xs text-gray-500">{vehicle.type || `${vehicle.seaters || 4} Seater`}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <Star size={12} className="fill-yellow-400 text-yellow-400" />
                    <span className="text-xs text-gray-600">4.9 • 1.2k rides</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Free Cancellation Banner */}
            <div className="bg-amber-50 rounded-xl p-3 mb-4 flex items-center gap-2 border border-amber-200">
              <span className="text-xl">⏱️</span>
              <p className="text-xs text-amber-800 flex-1">
                <strong>Free cancellation</strong> up to 1 hour before departure
              </p>
            </div>

            {/* Contact Details Section */}
            <div className="bg-white rounded-2xl shadow-lg mb-4 overflow-hidden animate-slideUp">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <User size={16} className="text-orange-600" />
                </div>
                <h2 className="font-bold text-gray-800">Contact Details</h2>
              </div>
              <div className="p-4 space-y-4">
                {/* Full Name */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Full Name *</label>
                  <input
                    className={`w-full mt-1 px-4 py-3 border ${errors.fullName ? 'border-red-400 bg-red-50' : 'border-gray-200'} rounded-xl text-sm focus:border-orange-400 transition`}
                    placeholder="Enter your full name"
                    value={form.fullName}
                    onChange={e => setForm(p => ({ ...p, fullName: e.target.value }))}
                  />
                  {errors.fullName && <p className="text-xs text-red-500 mt-1">{errors.fullName}</p>}
                </div>

                {/* Mobile Number */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Mobile Number *</label>
                  <div className="flex gap-2 mt-1">
                    <select
                      value={form.countryCode}
                      onChange={e => setForm(p => ({ ...p, countryCode: e.target.value }))}
                      className="w-20 px-3 py-3 border border-gray-200 rounded-xl text-sm bg-white"
                    >
                      <option>+91</option>
                      <option>+1</option>
                      <option>+44</option>
                    </select>
                    <input
                      className={`flex-1 px-4 py-3 border ${errors.mobile ? 'border-red-400 bg-red-50' : 'border-gray-200'} rounded-xl text-sm focus:border-orange-400 transition`}
                      placeholder="10-digit number"
                      value={form.mobile}
                      maxLength={10}
                      onChange={e => setForm(p => ({ ...p, mobile: e.target.value.replace(/\D/, '') }))}
                    />
                  </div>
                  {errors.mobile && <p className="text-xs text-red-500 mt-1">{errors.mobile}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Email ID *</label>
                  <input
                    className={`w-full mt-1 px-4 py-3 border ${errors.email ? 'border-red-400 bg-red-50' : 'border-gray-200'} rounded-xl text-sm focus:border-orange-400 transition`}
                    type="email"
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  />
                  {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}

                  {/* Extra Links */}
                  <div className="flex gap-4 mt-2">
                    <button
                      className="text-xs text-orange-500 font-medium flex items-center gap-1"
                      onClick={() => setShowAlt(p => !p)}
                    >
                      + Alternate email
                    </button>
                    <button
                      className="text-xs text-orange-500 font-medium flex items-center gap-1"
                      onClick={() => setShowGST(p => !p)}
                    >
                      + Add GST
                    </button>
                  </div>
                </div>

                {/* Alternate Email */}
                {showAlt && (
                  <div className="animate-fadeIn">
                    <label className="text-xs font-semibold text-gray-500 uppercase">Alternate Email</label>
                    <input
                      className="w-full mt-1 px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-orange-400 transition"
                      type="email"
                      placeholder="alternate@email.com"
                      value={form.altEmail}
                      onChange={e => setForm(p => ({ ...p, altEmail: e.target.value }))}
                    />
                  </div>
                )}

                {/* GSTIN */}
                {showGST && (
                  <div className="animate-fadeIn">
                    <label className="text-xs font-semibold text-gray-500 uppercase">GSTIN</label>
                    <input
                      className="w-full mt-1 px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-orange-400 transition"
                      placeholder="Enter GSTIN number"
                      value={form.gstin}
                      onChange={e => setForm(p => ({ ...p, gstin: e.target.value }))}
                    />
                  </div>
                )}

                {/* Pickup Instructions */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Pickup Instructions</label>
                  <textarea
                    className="w-full mt-1 px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-orange-400 transition"
                    rows="2"
                    placeholder="Any specific instructions for pickup location, landmarks, etc."
                    value={form.pickupInstructions}
                    onChange={e => setForm(p => ({ ...p, pickupInstructions: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Add-ons Section */}
            <div className="bg-white rounded-2xl shadow-lg mb-4 overflow-hidden animate-slideUp">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <Gift size={16} className="text-green-600" />
                </div>
                <h2 className="font-bold text-gray-800">Personalize Your Journey</h2>
              </div>
              <div className="p-4 space-y-3">
                {addonList.map(a => (
                  <div
                    key={a.id}
                    className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-all ${addons.includes(a.id) ? 'border-orange-400 bg-orange-50' : 'border-gray-200'
                      }`}
                    onClick={() => toggleAddon(a.id)}
                  >
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${addons.includes(a.id) ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
                      }`}>
                      {addons.includes(a.id) && <Check size={12} className="text-white" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">{a.label}</p>
                      {a.badge && <span className="text-xs text-orange-500 font-medium">{a.badge}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Info Card */}
            <div className="bg-green-50 rounded-2xl p-4 mb-4 border border-green-200">
              <div className="flex items-start gap-3">
                <span className="text-xl">💰</span>
                <div>
                  <p className="font-semibold text-green-800 text-sm">Pay to Driver at Pickup</p>
                  <p className="text-xs text-green-700 mt-1">
                    No online payment required. Please pay directly to the driver when the cab arrives.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sticky Bottom Button */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 shadow-lg z-50">
            <button
              onClick={handleProceed}
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-4 rounded-xl font-bold text-base active:scale-95 transition disabled:opacity-70 shadow-md"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  Confirming...
                </div>
              ) : (
                'CONFIRM BOOKING →'
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        /* Desktop Styles - Original */
        .cd-header {
          background: #1a1a2e;
          color: #fff;
          padding: 0 2rem;
        }
        .cd-header-inner {
          max-width: 1100px;
          margin: 0 auto;
          padding: 14px 0;
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .cd-header-back {
          background: rgba(255,255,255,.12);
          border: none;
          color: #fff;
          border-radius: 8px;
          padding: 6px 14px;
          font-size: 13px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          font-family: 'DM Sans', sans-serif;
        }
        .cd-header-title {
          font-family: 'Syne', sans-serif;
          font-size: 18px;
          font-weight: 700;
          letter-spacing: -.3px;
        }
        .cd-header-pill {
          background: rgba(255,255,255,.1);
          border-radius: 20px;
          padding: 4px 12px;
          font-size: 12px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .cd-free-cancel {
          background: #fffbeb;
          border-bottom: 1px solid #fde68a;
          text-align: center;
          padding: 8px;
          font-size: 12.5px;
          color: #92400e;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }
        .cd-body {
          max-width: 1100px;
          margin: 0 auto;
          padding: 24px 16px 40px;
          display: grid;
          grid-template-columns: 1fr 360px;
          gap: 24px;
          align-items: start;
        }
        .cd-card {
          background: #fff;
          border-radius: 16px;
          border: 1px solid #e8eaed;
          overflow: hidden;
          margin-bottom: 20px;
          box-shadow: 0 1px 4px rgba(0,0,0,.06);
        }
        .cd-card-head {
          padding: 16px 20px 14px;
          border-bottom: 1px solid #f0f0f0;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .cd-card-head-icon {
          width: 34px; height: 34px;
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          font-size: 16px;
        }
        .cd-card-head h2 {
          font-family: 'Syne', sans-serif;
          font-size: 15px;
          font-weight: 700;
          color: #111;
        }
        .cd-card-body { padding: 20px; }
        .cd-summary-strip {
          background: #1a1a2e;
          color: #fff;
          border-radius: 14px;
          padding: 16px 20px;
          margin-bottom: 20px;
        }
        .cd-summary-strip .route {
          font-family: 'Syne', sans-serif;
          font-size: 18px;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .cd-summary-strip .route span.arrow {
          color: #f97316;
        }
        .cd-summary-strip .sub {
          font-size: 12px;
          color: #94a3b8;
          margin-top: 6px;
        }
        .cd-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 10px;
        }
        .cd-chip {
          background: rgba(255,255,255,.1);
          border-radius: 20px;
          padding: 4px 10px;
          font-size: 11.5px;
          display: flex;
          align-items: center;
          gap: 5px;
          color: #cbd5e1;
        }
        .cd-form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }
        .cd-form-grid .full { grid-column: 1 / -1; }
        .cd-field label {
          display: block;
          font-size: 11.5px;
          font-weight: 600;
          color: #6b7280;
          margin-bottom: 5px;
          text-transform: uppercase;
          letter-spacing: .4px;
        }
        .cd-input {
          width: 100%;
          border: 1.5px solid #e5e7eb;
          border-radius: 10px;
          padding: 11px 14px;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          outline: none;
          transition: border .2s, box-shadow .2s;
          color: #111;
          background: #fafafa;
        }
        .cd-input:focus {
          border-color: #f97316;
          box-shadow: 0 0 0 3px rgba(249,115,22,.12);
          background: #fff;
        }
        .cd-input.err { border-color: #ef4444; background: #fff5f5; }
        .cd-err-msg { font-size: 11px; color: #ef4444; margin-top: 4px; }
        .cd-phone-row { display: flex; gap: 8px; }
        .cd-phone-row select {
          border: 1.5px solid #e5e7eb;
          border-radius: 10px;
          padding: 11px 10px;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          background: #fafafa;
          color: #111;
          outline: none;
          width: 90px;
          flex-shrink: 0;
        }
        .cd-link-btn {
          background: none;
          border: none;
          color: #f97316;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 0;
        }
        .cd-links-row {
          display: flex;
          gap: 16px;
          margin-top: 6px;
        }
        .addon-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 13px 16px;
          border: 1.5px solid #e5e7eb;
          border-radius: 12px;
          margin-bottom: 10px;
          cursor: pointer;
          transition: border .2s, background .2s;
        }
        .addon-item:hover { border-color: #f97316; background: #fff8f3; }
        .addon-item.active { border-color: #f97316; background: #fff3e6; }
        .addon-check {
          width: 20px; height: 20px;
          border-radius: 6px;
          border: 2px solid #d1d5db;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          transition: all .2s;
        }
        .addon-item.active .addon-check {
          background: #f97316; border-color: #f97316;
        }
        .addon-label { flex: 1; font-size: 13.5px; font-weight: 500; color: #111; }
        .addon-badge {
          background: #fff0e0;
          color: #ea580c;
          font-size: 10px;
          font-weight: 700;
          padding: 2px 7px;
          border-radius: 20px;
          margin-left: 6px;
        }
        .addon-price { font-size: 14px; font-weight: 700; color: #111; }
        .cd-pay-card { background: #fff; border-radius: 16px; border: 1px solid #e8eaed; overflow: hidden; box-shadow: 0 1px 4px rgba(0,0,0,.06); }
        .cd-pay-head {
          padding: 16px 20px;
          border-bottom: 1px solid #f0f0f0;
          font-family: 'Syne', sans-serif;
          font-size: 16px;
          font-weight: 700;
          color: #111;
        }
        .cd-pay-body { padding: 16px 20px; }
        .cd-fare-toggle {
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          color: #0ea5e9;
          font-size: 13px;
          font-weight: 700;
          padding: 10px 0;
          border-top: 1px solid #f0f0f0;
          margin-top: 4px;
        }
        .cd-fare-row {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          padding: 6px 0;
          color: #374151;
          border-bottom: 1px dashed #f3f4f6;
        }
        .cd-fare-row:last-child { border-bottom: none; }
        .cd-fare-total {
          display: flex;
          justify-content: space-between;
          font-size: 15px;
          font-weight: 800;
          padding: 12px 0 4px;
          border-top: 1.5px solid #e5e7eb;
          margin-top: 6px;
          font-family: 'Syne', sans-serif;
        }
        .cd-fare-total span:last-child { color: #ea580c; }
        .cd-proceed-btn {
          width: 100%;
          background: linear-gradient(135deg, #ea580c 0%, #f97316 100%);
          color: #fff;
          border: none;
          border-radius: 12px;
          padding: 15px;
          font-size: 15px;
          font-weight: 800;
          cursor: pointer;
          font-family: 'Syne', sans-serif;
          letter-spacing: .5px;
          margin-top: 16px;
          transition: transform .15s, box-shadow .15s;
          box-shadow: 0 4px 15px rgba(234,88,12,.35);
        }
        .cd-proceed-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(234,88,12,.4); }
        .cd-proceed-btn:active:not(:disabled) { transform: translateY(0); }
        .cd-proceed-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        .cd-vehicle-mini {
          display: flex;
          align-items: center;
          gap: 14px;
          background: #f8fafc;
          border-radius: 12px;
          padding: 12px 16px;
          margin-bottom: 18px;
          border: 1px solid #e5e7eb;
        }
        .cd-vehicle-mini img {
          width: 70px;
          height: 46px;
          object-fit: contain;
        }
        .cd-vehicle-mini .vname {
          font-weight: 700;
          font-size: 14px;
          color: #111;
        }
        .cd-vehicle-mini .vtype {
          font-size: 12px;
          color: #6b7280;
          margin-top: 2px;
        }
        .cd-vehicle-mini .vrating {
          margin-left: auto;
          background: #16a34a;
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          padding: 3px 8px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          gap: 3px;
        }
      `}</style>
    </>
  );
}
